import React, { useRef, useEffect } from 'react';
import { gsap } from '@titelmedia/bricks-utils';
import { Wrapper, ContainerDiv, Image, ImageWrapper } from './styles';

export const Horizontal = ({
  items,
  enableAnimation = false,
  setAnimationDuration = 0.75,
  fullHeight = false,
  imagePadding = true,
  customCss,
}) => {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !containerRef.current ||
      !wrapperRef.current
    )
      return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const { offsetWidth } = containerRef.current;

    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    let currentX = 0;
    const maxX = 0;
    const minX = -(offsetWidth - window.innerWidth);

    const ctx = gsap.context(() => {
      const { offsetWidth } = container;
      const isAnimationEnable =
        enableAnimation === 'true' || enableAnimation === true;

      gsap.effects.horizontal(container, {
        x: `-=${offsetWidth - window.innerWidth}px`,
        ease: 'power2.inOut',
        trigger: wrapper,
        end: `+=${offsetWidth}`,
        anticipatePin: 0.5,
      });

      if (isAnimationEnable && !prefersReducedMotion) {
        gsap.effects.fade(containerRef.current, {
          duration: setAnimationDuration,
          trigger: wrapper,
        });
      }
    });

    const handleKeyDown = e => {
      const scrollStep = 200;

      const transform = gsap.getProperty(container, 'x');
      currentX =
        typeof transform === 'number' ? transform : parseFloat(transform);

      if (e.key === 'ArrowRight') {
        currentX = Math.max(currentX - scrollStep, minX);
      } else if (e.key === 'ArrowLeft') {
        currentX = Math.min(currentX + scrollStep, maxX);
      } else {
        return;
      }

      gsap.to(container, {
        x: currentX,
        ease: 'power2.inOut',
        duration: 0.4,
      });
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      ctx.revert();
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableAnimation, setAnimationDuration]);

  function showContent() {
    if (items.length === 0) {
      console.error('No images on the items list in the Horizontal Gallery');
      return null;
    }

    return (
      <Wrapper ref={wrapperRef} customCss={customCss}>
        <ContainerDiv
          ref={containerRef}
          role="region"
          aria-label="Horizontal Image Gallery"
          tabIndex={0}
          firstImgAspectRatio={items[0]?.aspectRatio || 0}
          lastImgAspectRatio={items[items.length - 1]?.aspectRatio || 0}
          fullHeight={fullHeight}
        >
          {items.map((item, i) => (
            <ImageWrapper
              key={`image--${i}`}
              aspectRatio={item.aspectRatio}
              fullHeight={fullHeight}
              imagePadding={imagePadding}
            >
              <Image
                tabIndex={0}
                src={item.src}
                srcSet={item.srcSet}
                alt={item.alt || `Gallery Image ${i + 1}`}
                fullHeight={fullHeight}
              />
            </ImageWrapper>
          ))}
        </ContainerDiv>
      </Wrapper>
    );
  }

  return showContent();
};
